'use client';

import { Collection, Product } from '@/lib/shopify/types';
import Image from 'next/image';
import Link from 'next/link';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon, UserIcon } from '@heroicons/react/24/outline';
import { useCustomer } from '@/components/customer/customer-context';

// Shared menu configuration for both mobile and desktop
export const getMenuSections = (collections: Collection[], featuredItems: Product[], services: { title: string; path: string }[]) => {
  // Define our specific services with images
  const serviceItems = [
    { 
      title: '3D Printing Service', 
      path: '/3d-printing-service', 
      image: '/images/3d-printing/hero.png'
    },
    { 
      title: 'Laser Cutting Service', 
      path: '/laser-cutting-service', 
      image: '/images/laser-cutting/hero.png'
    },
    { 
      title: 'Business Services', 
      path: '/business-services', 
      image: '/images/business/hero.png'
    },
    { 
      title: 'Wedding Services', 
      path: '/wedding-services', 
      image: '/images/wedding/hero.png'
    }
  ];

  // Ensure featuredItems is an array before filtering
  const safeFeatureItems = Array.isArray(featuredItems) ? featuredItems : [];

  return [
    {
      id: 'shop',
      title: 'Shop',
      items: collections
        .filter(collection => !collection.handle.startsWith('hidden-'))
        .slice(0, 6)
        .map(collection => ({
          title: collection.title,
          path: collection.path
        }))
    },
    {
      id: 'services',
      title: 'Services',
      items: serviceItems
    },
    {
      id: 'community',
      title: 'Community',
      items: safeFeatureItems
        .filter(product => !product.handle.startsWith('hidden-'))
        .slice(0, 4)
        .map(product => ({
          title: product.title,
          path: `/product/${product.handle}`,
          image: product.featuredImage?.url,
          price: {
            amount: product.priceRange.minVariantPrice.amount,
            currencyCode: product.priceRange.minVariantPrice.currencyCode
          }
        }))
    }
  ];
};

interface ShadcnMobileMegaMenuProps {
  collections: Collection[];
  featuredItems: Product[];
  services: { title: string; path: string }[];
  closeMobileMenu: () => void;
}

export default function ShadcnMobileMegaMenu({
  collections,
  featuredItems,
  services,
  closeMobileMenu
}: ShadcnMobileMegaMenuProps) {
  const { customer, isAuthenticated, logout } = useCustomer();
  const menuSections = getMenuSections(collections, featuredItems, services);

  const handleLogout = async () => {
    await logout();
    closeMobileMenu();
  };

  return (
    <div className="mt-4 space-y-4 flex flex-col min-h-[calc(100vh-200px)]">
      <div className="flex-grow space-y-4">
        {menuSections.map((section) => (
          <Disclosure key={section.id}>
            {({ open }) => (
              <div>
                <Disclosure.Button className="flex w-full items-center justify-between rounded-md py-3 px-4 text-left text-lg font-medium text-white hover:bg-neutral-700">
                  <span>{section.title}</span>
                  <ChevronDownIcon
                    className={`${
                      open ? 'rotate-180 transform' : ''
                    } h-5 w-5 text-neutral-400`}
                  />
                </Disclosure.Button>
              <Disclosure.Panel className="px-4 pt-2 pb-4">
                {section.id === 'services' ? (
                  <div>
                    <div className="grid grid-cols-1 gap-4">
                      {section.items.map((item: any) => (
                        <Link
                          key={item.path}
                          href={item.path}
                          prefetch={true}
                          onClick={closeMobileMenu}
                          className="block overflow-hidden rounded-lg border border-neutral-700 transition-all hover:border-[#F1EFEC]"
                        >
                          <div className="relative h-36 w-full overflow-hidden">
                            <Image
                              src={item.image}
                              alt={item.title}
                              fill
                              sizes="(max-width: 768px) 100vw, 33vw"
                              className="object-cover transition-transform duration-300 hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
                              <div className="p-4 w-full text-white">
                                <h4 className="font-medium text-lg">{item.title}</h4>
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : section.id === 'featured' ? (
                  <div>
                    <div className="grid grid-cols-2 gap-4">
                      {section.items.map((item: any) => (
                        <Link
                          key={item.path}
                          href={item.path}
                          prefetch={true}
                          onClick={closeMobileMenu}
                          className="block overflow-hidden rounded-lg border border-neutral-700 transition-all hover:border-[#F1EFEC]"
                        >
                          {item.image && (
                            <div className="relative h-36 w-full overflow-hidden">
                              <Image
                                src={item.image}
                                alt={item.title}
                                fill
                                sizes="(max-width: 768px) 50vw, 33vw"
                                className="object-cover transition-transform duration-300 hover:scale-105"
                              />
                            </div>
                          )}
                          <div className="p-3">
                            <p className="text-sm font-medium text-white truncate">{item.title}</p>
                            {item.price && (
                              <p className="text-xs text-neutral-300">
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: item.price.currencyCode
                                }).format(parseFloat(item.price.amount))}
                              </p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-4 border-t border-neutral-700 pt-3">
                      <Link
                        href="/search"
                        prefetch={true}
                        onClick={closeMobileMenu}
                        className="block py-2 text-base font-bold text-neutral-300 hover:text-white"
                      >
                        View All Featured Items →
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div>
                    <ul className="space-y-2">
                      {section.items.map((item) => (
                        <li key={item.path}>
                          <Link
                            href={item.path}
                            prefetch={true}
                            onClick={closeMobileMenu}
                            className="flex items-center rounded-md px-4 py-3 text-base text-white hover:bg-neutral-700 hover:text-[#F1EFEC]"
                          >
                            {section.id === 'services' && (
                              <span className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-neutral-800 text-[#F1EFEC]">
                                {item.title === '3D Printing' && (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                                  </svg>
                                )}
                                {item.title === 'Laser Cutting' && (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2v8" />
                                    <path d="m4.93 10.93 1.41 1.41" />
                                    <path d="M2 18h2" />
                                    <path d="M20 18h2" />
                                    <path d="m19.07 10.93-1.41 1.41" />
                                    <path d="M22 22H2" />
                                    <path d="m16 6-4 4-4-4" />
                                    <path d="M16 18a4 4 0 0 0 0-8H8a4 4 0 0 0 0 8" />
                                  </svg>
                                )}
                                {item.title === 'Engraving' && (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 7h-4" />
                                    <path d="M3 21h18" />
                                    <path d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
                                  </svg>
                                )}
                                {item.title === 'Personalised Gifts' && (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 12v10H4V12" />
                                    <path d="M2 7h20v5H2z" />
                                    <path d="M12 22V7" />
                                    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                                    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                                  </svg>
                                )}
                                {!['3D Printing', 'Laser Cutting', 'Engraving', 'Personalised Gifts'].includes(item.title) && (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                                  </svg>
                                )}
                              </span>
                            )}
                            <span className="truncate">{item.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>
        ))}
      </div>

      {/* Account Section - Now at the bottom */}
      <div className="mt-auto border-t border-neutral-700 pt-6">
        {isAuthenticated ? (
          <div className="space-y-4 px-4">
            <div className="flex items-center gap-2 mb-4">
              <UserIcon className="h-5 w-5" />
              <h3 className="text-lg font-medium">Account</h3>
            </div>
            <div className="flex w-full items-center rounded-md bg-neutral-800 px-4 py-3 text-left font-medium text-white">
              <span className="text-base">
                {customer?.firstName || customer?.displayName || 'Account'}
              </span>
            </div>
            <div className="space-y-2">
              <Link
                href="/account"
                onClick={closeMobileMenu}
                className="block w-full rounded-md bg-neutral-700 px-4 py-2 text-left font-medium text-white hover:bg-neutral-600"
              >
                Account Settings
              </Link>
              <Link
                href="/orders"
                onClick={closeMobileMenu}
                className="block w-full rounded-md bg-neutral-700 px-4 py-2 text-left font-medium text-white hover:bg-neutral-600"
              >
                Orders
              </Link>
              <Link
                href="/account/orders"
                onClick={closeMobileMenu}
                className="block w-full rounded-md bg-neutral-700 px-4 py-2 text-left font-medium text-white hover:bg-neutral-600"
              >
                Order History (Account)
              </Link>
              <button
                onClick={handleLogout}
                className="w-full rounded-md bg-red-600 px-4 py-2 text-left font-medium text-white hover:bg-red-700"
              >
                Log Out
              </button>
            </div>
          </div>
        ) : (
          <div className="px-4">
            <div className="flex items-center gap-2 mb-4">
              <UserIcon className="h-5 w-5" />
              <h3 className="text-lg font-medium">Account</h3>
            </div>
            <button
              onClick={() => {
                closeMobileMenu();
                // Find and click the account modal button
                const accountButton = document.querySelector('[aria-label="Open account menu"]');
                if (accountButton) {
                  accountButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                } else {
                  // Fallback if button not found - redirect to homepage
                  window.location.href = '/';
                }
              }}
              className="block w-full rounded-md bg-neutral-700 px-4 py-2 text-center font-medium text-white hover:bg-neutral-600 mb-6"
            >
              Sign In / Register
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
